import React from 'react';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { Grid } from '@material-ui/core';
import { useFormikContext, getIn } from 'formik';

import Field from 'core/components/form/form.v2';
import { ActionButton } from 'core/components/action-button';
import { AppliedTag } from 'common/components/farm/applied-tag';
import { SubRiskCollapsibleSection } from './sub-risk-collapsible-section';
import useStyles from './sub-risk.styles';

const WellPumpForm = ({ fieldName, disabled }) => {
  const classes = useStyles();
  const { values, setFieldValue } = useFormikContext();

  const isApplied =
    !!getIn(values, `${fieldName}.building.numWellPumps`) ||
    !!getIn(values, `${fieldName}.building.wellPumpEstimatedValue`) ||
    !!getIn(values, `${fieldName}.building.wellPumpDescription`);

  // "Remove" an always-rendered coverage section = clear its fields back to the un-applied state (#32150).
  const handleRemove = () => {
    setFieldValue(`${fieldName}.building.numWellPumps`, null);
    setFieldValue(`${fieldName}.building.wellPumpEstimatedValue`, null);
    setFieldValue(`${fieldName}.building.wellPumpDescription`, null);
  };

  return (
    <SubRiskCollapsibleSection
      type="secondary"
      content={<div className={classes.headerText}>Well pump replacement value</div>}
      contentRight={
        isApplied ? (
          <div className={classes.appliedActions}>
            <AppliedTag />
            {!disabled && (
              <ActionButton
                type="delete"
                size="small"
                dataTestId="well-pump-remove"
                alt="Remove well pump replacement value coverage"
                onClick={(event) => {
                  event.stopPropagation();
                  handleRemove();
                }}
              />
            )}
          </div>
        ) : null
      }
      initialOpen={false}
    >
      <Grid container spacing={2}>
        <Field
          xs={12}
          type="numeric"
          id={`${fieldName}.building.numWellPumps`}
          name={`${fieldName}.building.numWellPumps`}
          mode="dark"
          label="Number of pumps"
          format={{
            allowNegative: false
          }}
          disabled={disabled}
        />
        <Field
          xs={12}
          type="numeric"
          id={`${fieldName}.building.wellPumpEstimatedValue`}
          name={`${fieldName}.building.wellPumpEstimatedValue`}
          mode="dark"
          label="Estimated value"
          description="We record this value for the underwriter"
          descriptionClassName={classes.fieldHelperText}
          format={{
            thousandSeparator: true,
            prefix: '$',
            allowNegative: false
          }}
          disabled={disabled}
        />
        <Field
          xs={12}
          type="textarea"
          id={`${fieldName}.building.wellPumpDescription`}
          name={`${fieldName}.building.wellPumpDescription`}
          mode="dark"
          label="Description of pumps or motors"
          disabled={disabled}
        />
      </Grid>
    </SubRiskCollapsibleSection>
  );
};

WellPumpForm.propTypes = {
  fieldName: PropTypes.string.isRequired,
  disabled: PropTypes.bool
};

WellPumpForm.defaultProps = {
  disabled: false
};

export default observer(WellPumpForm);
